import firebase from 'firebase/compat/app';
import 'firebase/compat/messaging';

import AsyncStorage from '@react-native-async-storage/async-storage';

import { centralAPI } from './central-api';
import { translate } from './translate';

type FirebaseNotification = ({
    occurrenceId: string;
} & ({
    type: 'ALARM_CENTER_OCCURRENCE';
    alarmCenterTitle: string;
} | {
    type: 'AREA_INVASION_OCCURRENCE' | 'PEOPLE_CROWD_OCCURRENCE';
    imageUrl: string;
    cameraTitle: string;
} | {
    type: 'GUARDIAN_APP_OCCURRENCE';
    womanName: string;
} | {
    type: 'IRREGULAR_VEHICLE_OCCURRENCE' | 'PLATE_DETECTED_OCCURRENCE' | 'CLONE_SUSPECT_OCCURRENCE' | 'ILLEGAL_DEBRIS_OCCURRENCE';
    imageUrl: string;
    platePosition: string;
    description: string;
} | {
    type: 'MISSING_FACE_DETECTED_OCCURRENCE' | 'MP_FACE_DETECTED_OCCURRENCE' | 'NEW_OCCURRENCE_FACE_DETECTED' | 'NOT_ALLOWED_FACE_DETECTED_OCCURRENCE';
    imageUrl: string;
} | {
    type: 'MP_MOBILE_AGENT_PLATE_DETECTED_OCCURRENCE' | 'MP_PLATE_DETECTED_OCCURRENCE' | 'CORTEX_PLATE_OCCURRENCE';
    imageUrl: string;
    plate: string;
} | {
    type: 'NEW_OCCURRENCE';
    occurrenceTypeName: string;
} | {
    type: 'SCENE_CHANGE_OCCURRENCE';
    cameraTitle: string;
} | {
    type: 'SIGRC_OCCURRENCE';
}));

class ExtendableError extends Error {
    constructor(message: string) {
        super(message);
        this.name = this.constructor.name;
        if (typeof Error.captureStackTrace === 'function') {
            Error.captureStackTrace(this, this.constructor);
        } else {
            this.stack = (new Error(message)).stack;
        }
    }
}

export class invalidFirebaseToken extends ExtendableError {
}

export class tryingToCreateFirebaseTokenBeforeAuth extends ExtendableError {
}

interface VehicleNotification {
    title: string;
    occurrenceId: string;
    imageUrl: string;
    platePosition: { x: number; y: number; }[];
    description?: string;
}

class NotificationService {
    token: string;
    messaging: firebase.messaging.Messaging | undefined;

    constructor() {
        this.token = '';

        try {

            const app = firebase.initializeApp({
                apiKey: process.env.FIREBASE_API_KEY,
                authDomain: process.env.FIREBASE_AUTH_DOMAIN,
                projectId: process.env.FIREBASE_PROJECT_ID,
                storageBucket: process.env.FIREBASE_STORAGE_BUCKET,
                messagingSenderId: process.env.FIREBASE_MESSAGING_SENDER_ID,
                appId: process.env.FIREBASE_APP_ID
            });

            this.messaging = firebase.messaging(app);

            this.messaging.onMessage(async (payload: firebase.messaging.MessagePayload) => {
                try {
                    if (!payload.data) {
                        return;
                    }
                    const data = payload.data as unknown as FirebaseNotification;
                    if (data.type === 'NEW_OCCURRENCE_FACE_DETECTED') {
                        return await this.occurrenceShowNotification({ title: translate('faceDetectedAlert'), data });
                    } else if (data.type == 'MISSING_FACE_DETECTED_OCCURRENCE') {
                        return await this.occurrenceShowNotification({ title: translate('missingFaceDetectedAlert'), data });
                    } else if (data.type == 'NEW_OCCURRENCE') {
                        return await this.occurrenceShowNotification({ title: translate('occurrence'), body: data.occurrenceTypeName, data });
                    } else if (data.type == 'SCENE_CHANGE_OCCURRENCE') {
                        return await this.occurrenceShowNotification({ title: translate('cameraDepredationSuspect'), body: data.cameraTitle, data });
                    } else if (data.type == 'IRREGULAR_VEHICLE_OCCURRENCE') {
                        let description = '';
                        if (data.description == 'on_phone') {
                            description = translate('onPhone');
                        }
                        if (data.description == 'without_belt') {
                            description = translate('withoutBelt');
                        }

                        return await this.vehicleNotification({
                            title: translate('irregular_vehicle'),
                            description: description,
                            occurrenceId: data.occurrenceId,
                            imageUrl: data.imageUrl,
                            platePosition: JSON.parse(data.platePosition)
                        });
                    } else if (data.type == 'CLONE_SUSPECT_OCCURRENCE') {
                        return await this.vehicleNotification({
                            title: translate('clone_suspect'),
                            description: data.description,
                            occurrenceId: data.occurrenceId,
                            imageUrl: data.imageUrl,
                            platePosition: JSON.parse(data.platePosition)
                        });
                    } else if (data.type == 'ILLEGAL_DEBRIS_OCCURRENCE') {
                        return await this.vehicleNotification({
                            title: translate('illegalDebrisAlert'),
                            description: data.description,
                            occurrenceId: data.occurrenceId,
                            imageUrl: data.imageUrl,
                            platePosition: JSON.parse(data.platePosition)
                        });
                    } else if (data.type == 'PLATE_DETECTED_OCCURRENCE') {
                        return await this.vehicleNotification({
                            title: translate('plateDetectedAlert'),
                            description: data.description,
                            occurrenceId: data.occurrenceId,
                            imageUrl: data.imageUrl,
                            platePosition: JSON.parse(data.platePosition)
                        });
                    } else if (data.type === 'AREA_INVASION_OCCURRENCE') {
                        return await this.occurrenceShowNotification({ title: translate('areaInvasionAlert'), data });
                    } else if (data.type === 'PEOPLE_CROWD_OCCURRENCE') {
                        return await this.occurrenceShowNotification({ title: translate('peopleCrowdAlert'), data });
                    } else if (data.type === 'MP_FACE_DETECTED_OCCURRENCE') {
                        return await this.occurrenceShowNotification({ title: translate('mpFaceDetectedAlert'), data });
                    } else if (data.type === 'MP_PLATE_DETECTED_OCCURRENCE') {
                        return await this.occurrenceShowNotification({
                            title: translate('mpPlateDetectedAlert'),
                            body: data.plate,
                            data
                        });
                    } else if (data.type === 'MP_MOBILE_AGENT_PLATE_DETECTED_OCCURRENCE') {
                        return await this.occurrenceShowNotification({
                            title: translate('mpMobileAgentPlateDetectedAlert'),
                            body: data.plate,
                            data
                        });
                    } else if (data.type === 'ALARM_CENTER_OCCURRENCE') {
                        return await this.occurrenceShowNotification({
                            title: translate('alarmCenterAlert'),
                            body: data.alarmCenterTitle,
                            data
                        });
                    } else if (data.type === 'NOT_ALLOWED_FACE_DETECTED_OCCURRENCE') {
                        return await this.occurrenceShowNotification({ title: translate('notAllowedFaceDetectedAlert'), data });
                    } else if (data.type === 'SIGRC_OCCURRENCE') {
                        return await this.occurrenceShowNotification({ title: translate('sigrcAlert'), data });
                    } else if (data.type === 'GUARDIAN_APP_OCCURRENCE') {
                        return await this.occurrenceShowNotification({
                            title: translate('guardianAppAlert'),
                            body: data.womanName,
                            data
                        });
                    } else if (data.type === 'CORTEX_PLATE_OCCURRENCE') {
                        return await this.vehicleNotification({
                            title: translate('cortexPlateDetectedAlert'),
                            occurrenceId: data.occurrenceId,
                            description: data.plate,
                            imageUrl: data.imageUrl,
                            platePosition: []
                        });
                    }
                } catch (err) {
                    console.error(err);
                }
            });
        } catch (err) {
            console.error(err);
        }
    }

    private async vehicleNotification({ title, occurrenceId, imageUrl, platePosition, description }: VehicleNotification) {
        const img = new Image();
        img.src = imageUrl;
        img.crossOrigin = 'anonymous';

        const serviceWorkerRegistration = await navigator.serviceWorker.ready;

        const lprImageWidth = 1920;
        const lprImageHeight = 1080;
        const lprImageRatio = lprImageWidth / lprImageHeight;

        if (platePosition.length == 0) {
            await serviceWorkerRegistration.showNotification(title, {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                image: imageUrl,
                body: description,
                icon: window.location.origin + '/favicon.ico',
                badge: window.location.origin + '/favicon.ico',
                data: {
                    clickAction: `/app-agent/occurrences/${occurrenceId}`
                },
            });
            return;
        }

        const rect = {
            minX: Infinity,
            maxX: 0,
            minY: Infinity,
            maxY: 0
        };

        for (const position of platePosition) {
            if (position.x < rect.minX) {
                rect.minX = position.x;
            }

            if (position.x > rect.maxX) {
                rect.maxX = position.x;
            }

            if (position.y < rect.minY) {
                rect.minY = position.y;
            }

            if (position.y > rect.maxY) {
                rect.maxY = position.y;
            }
        }

        const x = rect.minX;
        const y = rect.minY;
        const width = rect.maxX - rect.minX;
        const height = rect.maxY - rect.minY;

        const vehicleWidth = width * 4;
        const widthCenter = x + width - (width / 2);
        const heightCenter = y + height - (height / 2);


        const vehicleDimension = {
            x: widthCenter - (vehicleWidth / 2),
            y: heightCenter - (vehicleWidth / 2),
            width: vehicleWidth,
            height: vehicleWidth
        };

        if (vehicleDimension.width / vehicleDimension.height > lprImageRatio) {
            // based on vehicle width
            const oldHeight = vehicleDimension.height;
            vehicleDimension.height = vehicleDimension.width / lprImageRatio;
            const heightDiff = vehicleDimension.height - oldHeight;
            vehicleDimension.y -= (heightDiff / 2);

            if (vehicleDimension.height + vehicleDimension.y > lprImageHeight) {
                vehicleDimension.y -= vehicleDimension.height + vehicleDimension.y - lprImageHeight;
            }

            if (vehicleDimension.y < 0) {
                vehicleDimension.y = 0;
            }
        } else {
            // based on vehicle height
            const oldWidth = vehicleDimension.width;
            vehicleDimension.width = vehicleDimension.height * lprImageRatio;
            const widthDiff = vehicleDimension.width - oldWidth;
            vehicleDimension.x -= (widthDiff / 2);

            if (vehicleDimension.width + vehicleDimension.x > lprImageWidth) {
                vehicleDimension.x -= vehicleDimension.width + vehicleDimension.x - lprImageWidth;
            }

            if (vehicleDimension.x < 0) {
                vehicleDimension.x = 0;
            }
        }

        img.onload = async () => {
            const canvas = document.createElement('canvas');

            const ctx = canvas.getContext('2d');

            canvas.width = 1920;
            canvas.height = 1080;

            ctx?.drawImage(
                img,
                vehicleDimension.x,
                vehicleDimension.y,
                vehicleDimension.width,
                vehicleDimension.height,
                0,
                0,
                canvas.width,
                canvas.height
            );

            canvas.toBlob(async (blob) => {
                if (!blob) {
                    return;
                }
                const newUrl = URL.createObjectURL(blob);

                const serviceWorkerRegistration = await navigator.serviceWorker.ready;
                serviceWorkerRegistration.showNotification(title, {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    image: newUrl,
                    body: description,
                    icon: window.location.origin + '/favicon.ico',
                    badge: window.location.origin + '/favicon.ico',
                    data: {
                        clickAction: `/app-agent/occurrences/${occurrenceId}`
                    },

                });
            }, 'image/jpeg', 0.8);
        };

    }

    private async occurrenceShowNotification({ title, data, body }: { title: string; data: { occurrenceId: string; imageUrl?: string; }; body?: string; }) {
        const serviceWorkerRegistration = await navigator.serviceWorker.ready;

        await serviceWorkerRegistration.showNotification(title, {
            body: body !== undefined ? body : title,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            image: data.imageUrl,
            icon: window.location.origin + '/favicon.ico',
            badge: window.location.origin + '/favicon.ico',
            data: {
                clickAction: `/app-agent/occurrences/${data.occurrenceId}`
            },
        });
    }

    private async loadToken() {

        if (Notification.permission != 'granted') {
            await Notification.requestPermission();
        }

        if (Notification.permission != 'granted') {
            throw new invalidFirebaseToken(`The page doesn't have permission to access notifications.`);
        }

        let token;

        try {
            token = await this.messaging?.getToken({ vapidKey: process.env.FIREBASE_PUBLIC_VAPID_KEY });
        } catch (error) {
            // An error occurred while retrieving token.
            // BUT THE NEW TOKEN WILL PROBABLY BE SUCCESSFULLY FETCHED
            // note: idk why this happens :(
            token = await this.messaging?.getToken({ vapidKey: process.env.FIREBASE_PUBLIC_VAPID_KEY });
        }

        if (!token) {
            throw new invalidFirebaseToken('Invalid firebase token.');
        }

        this.token = token;
    }

    async registerToken() {

        if (!await centralAPI.isAuthenticated()) {
            throw new tryingToCreateFirebaseTokenBeforeAuth('You need to be authenticated to register firebase token.');
        }

        if (!this.token) {
            await this.loadToken();
        }

        const oldToken = await AsyncStorage.getItem('FIREBASE_TOKEN');

        if (oldToken != this.token) {
            await AsyncStorage.setItem('FIREBASE_TOKEN', this.token);
        }
        await centralAPI.registerUserDevice({
            firebaseToken: this.token
        });

    }

}

export const notificationService = new NotificationService();
